import { LiveWindow, conditions, durationWatched, getSubtitleLabel, videoTag } from "helpers";
import Hls from "hls.js";
import Image from "next/image";
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
    appendQueryParamsToChunksIOS,
    handleFragBuffered,
    handleFragChanged,
    handleFragLoading,
    handleFragParsed,
    handleHlsConfig,
    handleHlsErrors,
    handleIosVideoEnded,
    handleIosVideoPlay,
    handleIosVideoPlaying,
    handleIosVideoTimeUpdate,
    handleManifestLoaded,
    handleManifestParsed,
    handleMediaAttached,
    handleVideoCanPlay,
    handleVideoEnded,
    handleVideoError,
    handleVideoPause,
    handleVideoPlay,
    handleVideoPlaying,
    handleVideoSeeking,
    handleVideoTimeUpdate,
    handleVideoTimeline,
    handleVideoVisibilityChange,
    handleVideoWaiting
} from "../../../../../redux/actions";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { setIsEnded, setIsLive } from "../../../../../redux/slices/controllerSlice";
import usePingPong from "helpers/hooks/usePingPong";
import { ConditionName, ISubtitle } from "../../../../../interfaces";
import { handlePageLoadTime } from "../../../../../helpers";
import { setAutoPlayPopover, setRecommendListPopover } from "redux-share/slices/popoversSlice";
import { shallowEqual } from "react-redux";
import useBackButton from "helpers/hooks/useBackButton";
import useBeforeUnload from "helpers/hooks/useBeforeUnload";
import useRouteChange from "helpers/hooks/useRouteChange";
import { errorLogProvider } from "../../modules/video-player-library/players/errorBoundaryFunctions";
import useContinuousPlaybackInterval from "helpers/hooks/useContinuousPlaybackInterval";

const NonStaticVideoTag = () => {
    const [qualityLevel, setQualityLevel] = useState<number | undefined>(undefined);

    const dispatch = useAppDispatch();
    const { params, details, videoElementSizeInfo, videoTimelines } = useAppSelector(
        ({ videoData: { params, details, videoElementSizeInfo, isComplexPortrait, subtitles, videoTimelines } }) => ({
            params,
            details,
            videoElementSizeInfo,
            isComplexPortrait,
            subtitles,
            videoTimelines
        }),
        shallowEqual
    );
    const { hlsLiveSyncPosition, currentTime, duration } = useAppSelector(
        ({ controller: { hlsLiveSyncPosition, currentTime, duration, isEnded, firstPlay, showMiniPlayer } }) => ({
            hlsLiveSyncPosition,
            currentTime,
            duration,
            isEnded,
            firstPlay,
            showMiniPlayer
        }),
        shallowEqual
    );
    const timerRef = useRef<any>(null);
    const waitingDurationRef = useRef<any>(0);
    const waitingTimerRef = useRef<number>(0);
    const initialWaitingDurationRef = useRef<any>(0);
    const bannerLoadingDurationRef = useRef<any>(0);
    const loadingDurationRef = useRef<any>(0);
    const loadingCounterRef = useRef<any>(1);
    const videoEntranceTimeRef = useRef<any>(videoTag() ? videoTag().currentTime : 0);

    useBackButton();
    useBeforeUnload();
    useRouteChange();
    useContinuousPlaybackInterval();

    useEffect(() => {
        if (videoEntranceTimeRef.current === 0 && conditions(ConditionName.isLiveParam) && videoTag())
            videoEntranceTimeRef.current = videoTag().currentTime;
    }, [videoTag()]);

    useLayoutEffect(() => {
        let hls = new Hls(handleHlsConfig());

        // ---- Page Load Time ----
        handlePageLoadTime(loadingDurationRef);

        initialWaitingDurationRef.current = Date.now();

        if (Hls.isSupported()) {
            hls.attachMedia(videoTag() as HTMLMediaElement);
            hls.on(Hls.Events.MEDIA_ATTACHED, () => handleMediaAttached(Hls, hls));
            hls.on(Hls.Events.FRAG_BUFFERED, () => handleFragBuffered(hls));
            hls.on(Hls.Events.FRAG_LOADING, () => handleFragLoading(hls));
            hls.on(Hls.Events.FRAG_CHANGED, (event: any, data: any) => handleFragChanged(Hls, hls, data, setQualityLevel));
            hls.on(Hls.Events.FRAG_PARSED, (event: any, data: any) => handleFragParsed(hls, data, setQualityLevel));
            hls.on(Hls.Events.ERROR, (event: any, data: any) => errorLogProvider(handleHlsErrors(Hls, data)));

            videoTag().addEventListener("waiting", (event: any) => handleVideoWaiting(event, waitingTimerRef));
            videoTag().addEventListener("seeking", (event: any) => handleVideoSeeking(event, waitingTimerRef));
            videoTag().addEventListener("play", (event: any) => handleVideoPlay(event, timerRef));
            videoTag().addEventListener("playing", () =>
                handleVideoPlaying(
                    loadingCounterRef,
                    loadingDurationRef,
                    initialWaitingDurationRef,
                    bannerLoadingDurationRef,
                    timerRef,
                    waitingDurationRef,
                    waitingTimerRef,
                    videoEntranceTimeRef
                )
            );
            videoTag().addEventListener("pause", handleVideoPause);
            videoTag().addEventListener("timeupdate", handleVideoTimeUpdate);
            videoTag().addEventListener("canplay", handleVideoCanPlay);
            videoTag().addEventListener("ended", handleVideoEnded);
            videoTag().addEventListener("error", handleVideoError);

            document.addEventListener("visibilitychange", () => handleVideoVisibilityChange(hls));
        } else if (videoTag().canPlayType("application/vnd.apple.mpegurl")) {
            videoTag().src = conditions(ConditionName.isGeneral) ? params.src! : details.stream_link;

            videoTag().addEventListener("waiting", (event: any) => handleVideoWaiting(event, waitingTimerRef));
            videoTag().addEventListener("play", (event: any) => handleIosVideoPlay(event, timerRef));
            videoTag().addEventListener("playing", handleIosVideoPlaying);
            videoTag().addEventListener("canplay", handleVideoCanPlay);
            videoTag().addEventListener("timeupdate", handleIosVideoTimeUpdate);
            videoTag().addEventListener("ended", handleIosVideoEnded);
            videoTag().addEventListener("ended", handleVideoEnded);
            videoTag().addEventListener("error", handleVideoError);
            // Append Query Parameter to .ts Chunks
            appendQueryParamsToChunksIOS();
        }

        return () => {
            hls.detachMedia();
            hls.off(Hls.Events.MEDIA_ATTACHED, () => handleMediaAttached(Hls, hls));
            hls.off(Hls.Events.FRAG_BUFFERED, () => handleFragBuffered(hls));
            hls.off(Hls.Events.FRAG_LOADING, () => handleFragLoading(hls));
            hls.off(Hls.Events.FRAG_CHANGED, (event: any, data: any) => handleFragChanged(Hls, hls, data, setQualityLevel));
            hls.off(Hls.Events.FRAG_PARSED, (event: any, data: any) => handleFragParsed(hls, data, setQualityLevel));
            hls.off(Hls.Events.ERROR, (event: any, data: any) => handleHlsErrors(Hls, data));
            hls.off(Hls.Events.MANIFEST_PARSED, (event: any, data: any) => handleManifestParsed(hls, data));
            hls.off(Hls.Events.MANIFEST_LOADED, () => handleManifestLoaded(hls));
            document.removeEventListener("visibilitychange", () => handleVideoVisibilityChange(hls));
        };
    }, [details.stream_link]);
    useEffect(() => {
        if (conditions(ConditionName.isLiveParam) && videoTag()) {
            if (videoTag().currentTime < hlsLiveSyncPosition - LiveWindow) dispatch(setIsLive(false));
            else dispatch(setIsLive(true));
        }
    }, [hlsLiveSyncPosition]);

    useEffect(() => {
        const tracks = videoTag().getElementsByTagName("track");
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].addEventListener("cuechange", (event) => {
                const track = (event.target! as any).track;
                let activeCue: any;
                if (track.activeCues) activeCue = track.activeCues[0];
                if (activeCue) {
                    setTimeout(() => {
                        const el = document.getElementById("subtitle-id");
                        if (el) el.style.display = "block";
                    }, 300);

                    activeCue.onexit = () => {
                        const el = document.getElementById("subtitle-id");
                        if (el) el.style.display = "none";
                    };
                }
            });
        }
    }, [details.subtitles!]);

    useEffect(() => {
        if (Math.floor(Math.abs(videoTimelines[0] - currentTime)) === durationWatched) handleVideoTimeline(currentTime, currentTime);
        if (currentTime < duration && conditions(ConditionName.isEnded)) {
            dispatch(setIsEnded(false));
            dispatch(setAutoPlayPopover(false));
            dispatch(setRecommendListPopover(false));
        }
    }, [currentTime]);

    usePingPong(qualityLevel);

    return (
        <>
            {conditions(ConditionName.showBanner) && (
                <Image
                    src={details.banner}
                    width={0}
                    height={0}
                    className="pl-hidden"
                    onLoad={() => (bannerLoadingDurationRef.current = Date.now())}
                    alt={details.title ?? params.id!}
                />
            )}

            <video
                preload="metadata"
                crossOrigin="anonymous"
                id="player-tag"
                playsInline
                muted={conditions(ConditionName.isRaw)}
                className={`pl-items-center pl-flex-1 ${
                    !conditions(ConditionName.hasQuizParams) && (conditions(ConditionName.isComplexPortrait) ? "pl-h-auto" : "pl-h-full")
                }`}
                style={{
                    width: "100%",
                    opacity: conditions(ConditionName.isFirstPlay) ? 0 : 1,
                    height: conditions(ConditionName.hasQuizParams) ? "100vh" : "100%",
                    maxWidth: videoElementSizeInfo.width ? videoElementSizeInfo.width : "100%",
                    maxHeight: videoElementSizeInfo.width
                        ? videoElementSizeInfo.width < videoElementSizeInfo.height
                            ? (videoElementSizeInfo.width * 9) / 16
                            : (videoElementSizeInfo.width * 16) / 9
                        : "100%"
                }}
                autoPlay={conditions(ConditionName.hasAutoPlay) || conditions(ConditionName.isRaw)}
            >
                {conditions(ConditionName.hasSubtitles) &&
                    details.subtitles!.map((subtitle: ISubtitle) => (
                        <track
                            id={"player-tracker"}
                            key={subtitle.id}
                            src={subtitle.url}
                            label={getSubtitleLabel(subtitle.lang)}
                            kind="subtitles"
                            srcLang={subtitle.lang}
                            default={subtitle.lang === "fa"}
                        />
                    ))}
            </video>
        </>
    );
};

export default NonStaticVideoTag;
